import React, { useMemo, useState } from 'react';
import { BarChart3, AlertTriangle, CheckCircle, XCircle, Download, EyeOff } from 'lucide-react';

/**
 * Database Quality Dashboard
 * Shows real-time quality metrics for material matching
 * Toggle with Ctrl+Shift+D
 */
const DatabaseQualityDashboard = ({ materials, onExport }) => {
  const [isMinimized, setIsMinimized] = useState(false);

  // Calculate stats from materials - using useMemo to avoid effect issues
  const stats = React.useMemo(() => {
    if (!materials || materials.length === 0) {
      return {
        total: 0,
        perfect: 0,
        good: 0,
        fair: 0,
        poor: 0,
        notFound: 0,
        matchRate: 0
      };
    }

    const perfect = materials.filter(m => m.match_score === 1.0).length;
    const good = materials.filter(m => m.match_score >= 0.90 && m.match_score < 1.0).length;
    const fair = materials.filter(m => m.match_score >= 0.80 && m.match_score < 0.90).length;
    const poor = materials.filter(m => m.match_score >= 0.70 && m.match_score < 0.80).length;
    const notFound = materials.filter(m => m.match_score < 0.70 || m.match_score === 0).length;

    return {
      total: materials.length,
      perfect,
      good,
      fair,
      poor,
      notFound,
      matchRate: ((perfect + good) / materials.length * 100).toFixed(1)
    };
  }, [materials]);

  const getStatusColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 95) return 'text-emerald-400';
    if (numRate >= 85) return 'text-yellow-400';
    return 'text-red-400';
  };

  const exportGapReport = () => {
    if (!materials || materials.length === 0) return;
    
    // Filter items that need attention (score < 0.90)
    const gaps = materials.filter(m => m.match_score < 0.90);
    
    if (gaps.length === 0) {
      alert('No gaps to export! All items matched well.');
      return;
    }
    
    const report = gaps.map(item => ({
      item_name: item.item_name || item.ai_item_name || 'Unknown',
      quantity: item.quantity || 0,
      unit: item.unit || 'EA',
      match_score: (item.match_score || 0).toFixed(2),
      matched_to: item.match_score > 0 ? (item.item_name || 'PARTIAL') : 'NOT FOUND',
      category: item.category || 'unknown',
      suggested_cost: item.unit_cost || 'MISSING',
      action_needed: item.match_score === 0 ? 'ADD TO DATABASE' : 'IMPROVE ALIASES'
    }));

    // Convert to CSV
    const headers = Object.keys(report[0]);
    const csv = [
      headers.join(','),
      ...report.map(row => 
        headers.map(h => {
          const val = row[h];
          // Escape commas and quotes in CSV
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        }).join(',')
      )
    ].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `database_gaps_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    if (onExport) {
      onExport(gaps);
    }
  };

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 w-12 h-12 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center shadow-xl z-50 hover:bg-slate-700 transition-colors"
        title="Show Database Quality Dashboard (Ctrl+Shift+D)"
      >
        <BarChart3 className="w-5 h-5 text-emerald-400" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 font-mono">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-slate-700">
        <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2 uppercase tracking-wider">
          <BarChart3 className="w-4 h-4 text-emerald-400" />
          Database Quality
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={exportGapReport}
            disabled={stats.total === 0}
            className="text-[10px] bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:text-slate-500 px-2 py-1 rounded flex items-center gap-1 text-white transition-colors"
            title="Export items needing attention"
          >
            <Download className="w-3 h-3" />
            Export Gaps
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-white rounded hover:bg-slate-700 transition-colors"
            title="Minimize"
          >
            <EyeOff className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Overall Score */}
        <div className="p-3 bg-slate-900/50 rounded border border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Match Rate</div>
          <div className={`text-2xl font-bold ${getStatusColor(stats.matchRate)}`}>
            {stats.total > 0 ? `${stats.matchRate}%` : '—'}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {stats.total > 0 
              ? `${stats.perfect + stats.good} / ${stats.total} items matched well`
              : 'Upload a blueprint to see metrics'
            }
          </div>
        </div>

        {/* Breakdown */}
        {stats.total > 0 && (
          <div className="space-y-1.5">
            <StatRow
              label="Perfect (1.00)"
              count={stats.perfect}
              total={stats.total}
              icon={<CheckCircle className="w-3 h-3 text-emerald-400" />}
              color="bg-emerald-500"
            />
            
            <StatRow
              label="Good (0.90-0.99)"
              count={stats.good}
              total={stats.total}
              icon={<CheckCircle className="w-3 h-3 text-emerald-300" />}
              color="bg-emerald-400"
            />
            
            <StatRow
              label="Fair (0.80-0.89)"
              count={stats.fair}
              total={stats.total}
              icon={<AlertTriangle className="w-3 h-3 text-yellow-400" />}
              color="bg-yellow-500"
            />
            
            <StatRow
              label="Poor (0.70-0.79)"
              count={stats.poor}
              total={stats.total}
              icon={<AlertTriangle className="w-3 h-3 text-orange-400" />}
              color="bg-orange-500"
            />
            
            <StatRow
              label="Not Found (<0.70)"
              count={stats.notFound}
              total={stats.total}
              icon={<XCircle className="w-3 h-3 text-red-400" />}
              color="bg-red-500"
            />
          </div>
        )}

        {/* Action Items */}
        {stats.notFound > 0 && (
          <div className="p-2 bg-red-500/10 border border-red-500/30 rounded">
            <div className="text-[10px] text-red-400 font-bold flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {stats.notFound} items need attention!
            </div>
            <div className="text-[10px] text-red-300/70 mt-0.5">
              Export gaps to get action list
            </div>
          </div>
        )}

        {/* Success State */}
        {stats.total > 0 && parseFloat(stats.matchRate) >= 95 && (
          <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
            <div className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Excellent database coverage!
            </div>
            <div className="text-[10px] text-emerald-300/70 mt-0.5">
              95%+ match rate achieved
            </div>
          </div>
        )}

        {/* Keyboard Shortcut Hint */}
        <div className="text-[9px] text-slate-600 text-center pt-1 border-t border-slate-700/50">
          Press <kbd className="px-1 py-0.5 bg-slate-700 rounded text-slate-400">Ctrl+Shift+D</kbd> to toggle
        </div>
      </div>
    </div>
  );
};

const StatRow = ({ label, count, total, icon, color }) => {
  const percentage = total > 0 ? (count / total * 100).toFixed(0) : 0;
  
  return (
    <div className="flex items-center justify-between text-[10px]">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-slate-400">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-slate-300 w-6 text-right">{count}</span>
        <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full ${color} transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default DatabaseQualityDashboard;
